import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './Icon';
import { Button } from './Button';
import { Badge } from './Badge';
import { Modal } from './Modal';
import {
  containerFilesService,
  type FileNodeRemote,
  type FileModificationRemote,
} from '@/deploy/services/container-files.service';
import styles from './ConsoleModal.module.scss';

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: string;
  containerCode: string;
  containerColor: string;
  containerName: string;
  containerRepo: string;
  containerVersion: string;
  onModsChanged?: () => void;
  /** Rendu intégré dans la page (section) au lieu d'une modale overlay. */
  embedded?: boolean;
  /** Incrémenter cette valeur force un rechargement de l'arbre (ex: revert déclenché côté parent). */
  refreshSignal?: number;
}

function parentPath(path: string) {
  if (!path || path === '/') return '/';
  const i = path.lastIndexOf('/');
  if (i <= 0) return '/';
  return path.slice(0, i);
}
function baseName(path: string) {
  if (!path || path === '/') return '/';
  return path.slice(path.lastIndexOf('/') + 1);
}

export function ContainerFilesModal({
  open, onClose,
  projectId, containerCode,
  containerColor, containerName, containerRepo, containerVersion,
  onModsChanged, embedded = false, refreshSignal,
}: Props) {
  const [tree, setTree] = useState<FileNodeRemote | null>(null);
  const [mods, setMods] = useState<FileModificationRemote[]>([]);
  const [treeError, setTreeError] = useState<string | null>(null);
  const [loadingTree, setLoadingTree] = useState(false);

  const [selectedPath, setSelectedPath] = useState<string>('');
  const [openFolders, setOpenFolders] = useState<Set<string>>(new Set(['/']));
  const [content, setContent] = useState<string | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [renaming, setRenaming] = useState(false);

  const [newFolderFor, setNewFolderFor] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFileFor, setNewFileFor] = useState<string | null>(null);
  const [newFileName, setNewFileName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  // === Multi-sélection pour delete en masse ===
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState<{ done: number; total: number; errors: string[] } | null>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  // Refs stables sur les callbacks parent — évite que reloadTree (et donc l'effet de
  // mount) ne se re-déclenche à chaque render du parent (boucle infinie).
  const onCloseRef = useRef(onClose);
  const onModsChangedRef = useRef(onModsChanged);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => { onModsChangedRef.current = onModsChanged; }, [onModsChanged]);

  const reloadTree = useCallback(async () => {
    setLoadingTree(true);
    setTreeError(null);
    try {
      const [t, m] = await Promise.all([
        containerFilesService.tree(projectId, containerCode),
        containerFilesService.listMods(projectId, containerCode),
      ]);
      setTree(t);
      setMods(m);
      onModsChangedRef.current?.();
    } catch (e) {
      setTreeError(e instanceof Error ? e.message : 'Erreur');
      setTree(null);
    } finally {
      setLoadingTree(false);
    }
  }, [projectId, containerCode]);

  // Charge l'arbre quand la modale s'ouvre (ou au montage en mode intégré).
  // Le scroll-lock + Escape ne concernent que la modale overlay.
  useEffect(() => {
    if (!open && !embedded) return;
    void reloadTree();
    if (embedded) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCloseRef.current(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open, embedded, reloadTree]);

  // Rechargement déclenché par le parent (ex: revert d'une modif depuis la liste des
  // modifications en attente). Recharge l'arbre + les mods ; comme `tree` change de
  // référence, selectedNode est recalculé et le contenu du fichier sélectionné est
  // relu automatiquement (l'effet plus bas). On ignore le tout premier rendu.
  const reloadTreeRef = useRef(reloadTree);
  useEffect(() => { reloadTreeRef.current = reloadTree; }, [reloadTree]);
  const skipFirstRefresh = useRef(true);
  useEffect(() => {
    if (skipFirstRefresh.current) { skipFirstRefresh.current = false; return; }
    void reloadTreeRef.current();
  }, [refreshSignal]);

  // Re-synchronise le CONTENU du fichier ouvert après un refresh externe (ex: revert d'un mod
  // depuis la liste côté parent) — mais SEULEMENT si aucune édition non sauvegardée en cours,
  // sinon on écraserait le travail. (L'effet de chargement principal ne dépend pas de `tree`.)
  const selectedPathRef = useRef(selectedPath);
  const dirtyRef = useRef(false);
  useEffect(() => { selectedPathRef.current = selectedPath; }, [selectedPath]);
  useEffect(() => { dirtyRef.current = content != null && editContent !== content; }, [editContent, content]);
  const skipFirstContentSync = useRef(true);
  useEffect(() => {
    if (skipFirstContentSync.current) { skipFirstContentSync.current = false; return; }
    const p = selectedPathRef.current;
    if (!p || dirtyRef.current) return;
    containerFilesService.readText(projectId, containerCode, p)
      .then((c) => { setContent(c); setEditContent(c); })
      .catch(() => { /* fichier devenu illisible/supprimé → l'effet principal gère via selectedNode */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  const findByPath = useCallback((root: FileNodeRemote | null, target: string): FileNodeRemote | null => {
    if (!root) return null;
    if ((root.path ?? '/') === target) return root;
    if (!root.children) return null;
    for (const c of root.children) {
      const childPath = c.path ?? (root.path === '/' ? '/' + c.name : (root.path + '/' + c.name));
      if (childPath === target) return c;
      if (target.startsWith(childPath + '/')) {
        const nested = findByPath(c, target);
        if (nested) return nested;
      }
    }
    return null;
  }, []);

  const selectedNode = useMemo(() => selectedPath ? findByPath(tree, selectedPath) : null, [tree, selectedPath, findByPath]);
  const selectedIsFile = selectedNode?.type === 'file';

  // Charger le contenu quand on sélectionne un fichier text
  useEffect(() => {
    if (!selectedNode || selectedNode.type !== 'file') {
      setContent(null);
      setContentError(null);
      return;
    }
    if (selectedNode.readonly) {
      setContent(null);
      setContentError(null);
      return;
    }
    setLoadingContent(true);
    setContentError(null);
    setContent(null); // tant que le nouveau fichier n'est pas chargé : pas de buffer (évite d'écrire A dans B)
    containerFilesService.readText(projectId, containerCode, selectedPath)
      .then((c) => {
        setContent(c);
        setEditContent(c); // édition directe : le buffer suit le contenu chargé
      })
      .catch((e) => {
        setContent(null);
        setEditContent('');
        setContentError(e instanceof Error ? e.message : 'Erreur de lecture');
      })
      .finally(() => setLoadingContent(false));
    // Dépendances en primitives (chemin/type/readonly) et NON sur l'objet selectedNode :
    // un rechargement d'arbre en arrière-plan recrée selectedNode par référence sans changer le
    // fichier sélectionné — on ne relit donc PAS le contenu ici (préserve une édition en cours).
    // La re-synchro après un revert externe est gérée par l'effet dédié sur refreshSignal (si non dirty).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPath, selectedNode?.type, selectedNode?.readonly, projectId, containerCode]);

  const targetFolderForUpload = useMemo(() => {
    if (!selectedPath) return '/';
    if (selectedNode?.type === 'folder') return selectedPath;
    return parentPath(selectedPath);
  }, [selectedPath, selectedNode]);

  if (!open && !embedded) return null;

  const toggleFolder = (path: string) => {
    setOpenFolders((p) => { const n = new Set(p); if (n.has(path)) n.delete(path); else n.add(path); return n; });
  };
  const selectPath = (p: string) => { setSelectedPath(p); };

  const saveEdit = async () => {
    if (!selectedPath || content == null) return; // pas de contenu chargé → rien à sauver (évite d'écraser un autre fichier)
    setSaving(true);
    try {
      await containerFilesService.writeText(projectId, containerCode, selectedPath, editContent);
      setContent(editContent);
      await reloadTree();
    } catch (e) {
      alert('Sauvegarde échouée: ' + (e instanceof Error ? e.message : 'inconnu'));
    } finally {
      setSaving(false);
    }
  };

  // Renomme le fichier OU dossier sélectionné (backend = supprime l'ancien + ajoute le nouveau).
  const renameSelected = async () => {
    if (!selectedNode || !selectedPath || selectedPath === '/') return;
    const oldName = selectedNode.name;
    const next = window.prompt(`Renommer « ${oldName} » en :`, oldName);
    if (next == null) return;
    const to = next.trim();
    if (!to || to === oldName) return;
    if (to.includes('/')) { alert('Le nom ne doit pas contenir « / ».'); return; }
    const parent = parentPath(selectedPath);
    const newPath = parent === '/' ? '/' + to : parent + '/' + to;
    setRenaming(true);
    try {
      await containerFilesService.rename(projectId, containerCode, selectedPath, newPath);
      setSelectedPath(newPath);
      await reloadTree();
    } catch (e) {
      alert('Renommage échoué: ' + (e instanceof Error ? e.message : 'inconnu'));
    } finally {
      setRenaming(false);
    }
  };

  const downloadSelected = () => {
    if (!selectedNode || selectedNode.type !== 'file' || !selectedPath) return;
    containerFilesService.downloadBinary(projectId, containerCode, selectedPath);
  };

  const triggerUpload = () => uploadInputRef.current?.click();
  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    const tgt = targetFolderForUpload;
    setSaving(true);
    try {
      for (const f of files) {
        await containerFilesService.upload(projectId, containerCode, tgt, f);
      }
      await reloadTree();
    } catch (err) {
      alert('Upload échoué: ' + (err instanceof Error ? err.message : 'inconnu'));
    } finally {
      setSaving(false);
      if (uploadInputRef.current) uploadInputRef.current.value = '';
    }
  };

  const confirmNewFolder = async () => {
    if (!newFolderFor || !newFolderName.trim()) return;
    const clean = newFolderName.trim().replace(/\//g, '-');
    const folderPath = newFolderFor === '/' ? '/' + clean : newFolderFor + '/' + clean;
    setSaving(true);
    try {
      await containerFilesService.createFolder(projectId, containerCode, folderPath);
      setOpenFolders((p) => new Set(p).add(newFolderFor));
      setNewFolderName('');
      setNewFolderFor(null);
      await reloadTree();
    } catch (e) {
      alert('Création échouée: ' + (e instanceof Error ? e.message : 'inconnu'));
    } finally {
      setSaving(false);
    }
  };

  const confirmNewFile = async () => {
    if (!newFileFor || !newFileName.trim()) return;
    const clean = newFileName.trim().replace(/\//g, '-');
    const filePath = newFileFor === '/' ? '/' + clean : newFileFor + '/' + clean;
    setSaving(true);
    try {
      // Création = writeText avec contenu vide. Le backend crée le fichier s'il n'existe pas.
      await containerFilesService.writeText(projectId, containerCode, filePath, '');
      setOpenFolders((p) => new Set(p).add(newFileFor));
      setNewFileName('');
      setNewFileFor(null);
      await reloadTree();
      // Sélectionne le nouveau fichier (déjà éditable : textarea directe)
      setSelectedPath(filePath);
      setContent('');
      setEditContent('');
    } catch (e) {
      alert('Création échouée: ' + (e instanceof Error ? e.message : 'inconnu'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await containerFilesService.deletePath(projectId, containerCode, deleteTarget);
      if (selectedPath === deleteTarget || selectedPath.startsWith(deleteTarget + '/')) {
        setSelectedPath('');
      }
      setDeleteTarget(null);
      await reloadTree();
    } catch (e) {
      alert('Suppression échouée: ' + (e instanceof Error ? e.message : 'inconnu'));
    } finally {
      setSaving(false);
    }
  };

  // === Multi-select : toggle un path dans la sélection ===
  const toggleSelectionFor = (path: string) => {
    if (path === '/') return; // pas de suppression de la racine
    setSelectedForDelete((prev) => {
      const n = new Set(prev);
      if (n.has(path)) n.delete(path); else n.add(path);
      return n;
    });
  };

  const exitMultiSelect = () => {
    setMultiSelectMode(false);
    setSelectedForDelete(new Set());
  };

  // === Bulk delete : itère + suit la progression ===
  const confirmBulkDelete = async () => {
    const paths = Array.from(selectedForDelete);
    if (paths.length === 0) return;
    // Trie pour supprimer les enfants AVANT les parents (évite "introuvable" après suppression du parent)
    paths.sort((a, b) => b.length - a.length);
    setBulkDeleteProgress({ done: 0, total: paths.length, errors: [] });
    setSaving(true);
    const errors: string[] = [];
    let done = 0;
    for (const p of paths) {
      try {
        await containerFilesService.deletePath(projectId, containerCode, p);
      } catch (e) {
        errors.push(`${p}: ${e instanceof Error ? e.message : 'KO'}`);
      }
      done++;
      setBulkDeleteProgress({ done, total: paths.length, errors });
    }
    await reloadTree();
    setSaving(false);
    // Si tout est OK, ferme. Sinon laisse la modal ouverte pour afficher les erreurs
    if (errors.length === 0) {
      setBulkDeleteOpen(false);
      exitMultiSelect();
      setBulkDeleteProgress(null);
    }
    // Reset sélection sur les paths supprimés OK
    setSelectedForDelete((prev) => {
      const n = new Set(prev);
      for (const p of paths) {
        if (!errors.some((e) => e.startsWith(p + ':'))) n.delete(p);
      }
      return n;
    });
  };

  const modByPath = (path: string): FileModificationRemote | undefined => mods.find((m) => m.path === path);

  const shell = (
    <>
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.chip} style={{ background: containerColor }}>
              <Icon name="package" size={14} />
            </span>
            <div className={styles.headerText}>
              <div className={styles.headerTitle}>
                {containerName} · fichiers
                {mods.length > 0 && <Badge tone="warning">{mods.length} modif{mods.length > 1 ? 's' : ''} en attente</Badge>}
              </div>
              <div className={styles.headerSub}>{containerRepo} · {containerVersion}</div>
            </div>
          </div>
          <div className={styles.headerActions}>
            <button className={styles.iconBtn} onClick={() => void reloadTree()} title="Recharger" disabled={loadingTree}>
              <Icon name="refresh" size={12} />
            </button>
            {!embedded && (
              <button className={styles.closeBtn} onClick={onClose} aria-label="Fermer">
                <Icon name="x" size={16} />
              </button>
            )}
          </div>
        </header>

        {treeError && (
          <div style={{ padding: 12, background: 'var(--color-danger-bg)', color: 'var(--color-danger)', fontSize: 13 }}>
            {treeError}
          </div>
        )}

        <div className={styles.filesLayout}>
          <aside className={styles.treePane}>
            <div className={styles.treeToolbar}>
              <span className={styles.treeToolbarLabel}>Arborescence</span>
              <div className={styles.treeToolbarActions}>
                <button className={styles.iconBtn} onClick={triggerUpload} title={`Upload dans ${targetFolderForUpload}`} disabled={saving || multiSelectMode}>
                  <Icon name="deploy" size={12} />
                </button>
                <button
                  className={styles.iconBtn}
                  onClick={() => { setNewFileFor(targetFolderForUpload); setNewFileName(''); }}
                  title={`Nouveau fichier texte dans ${targetFolderForUpload}`}
                  disabled={saving || multiSelectMode}
                >
                  <Icon name="git-commit" size={12} />
                </button>
                <button
                  className={styles.iconBtn}
                  onClick={() => { setNewFolderFor(targetFolderForUpload); setNewFolderName(''); }}
                  title={`Nouveau dossier dans ${targetFolderForUpload}`}
                  disabled={saving || multiSelectMode}
                >
                  <Icon name="plus" size={12} />
                </button>
                <button
                  className={styles.iconBtn}
                  onClick={() => multiSelectMode ? exitMultiSelect() : setMultiSelectMode(true)}
                  title={multiSelectMode ? "Quitter la sélection multiple" : "Mode sélection multiple (delete en masse)"}
                  disabled={saving}
                  style={multiSelectMode ? { background: 'var(--color-primary)', color: 'white', borderColor: 'var(--color-primary)' } : undefined}
                >
                  <Icon name="check" size={12} />
                </button>
              </div>
            </div>
            <input ref={uploadInputRef} type="file" multiple style={{ display: 'none' }} onChange={onUpload} />

            {multiSelectMode && (
              <div style={{
                padding: '8px 10px',
                background: 'var(--color-primary-light)',
                borderBottom: '1px solid var(--color-border)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12,
              }}>
                <Icon name="check" size={12} />
                <strong>{selectedForDelete.size}</strong> sélectionné{selectedForDelete.size > 1 ? 's' : ''}
                <div style={{ flex: 1 }} />
                <button
                  className={styles.iconBtn}
                  onClick={() => setSelectedForDelete(new Set())}
                  disabled={saving || selectedForDelete.size === 0}
                  title="Désélectionner tout"
                  style={{ fontSize: 11 }}
                >
                  Clear
                </button>
                <button
                  className={[styles.iconBtn].join(' ')}
                  onClick={() => setBulkDeleteOpen(true)}
                  disabled={saving || selectedForDelete.size === 0}
                  title={`Supprimer ${selectedForDelete.size} élément${selectedForDelete.size > 1 ? 's' : ''}`}
                  style={{
                    background: selectedForDelete.size > 0 ? 'var(--color-danger)' : undefined,
                    color: selectedForDelete.size > 0 ? 'white' : undefined,
                    borderColor: selectedForDelete.size > 0 ? 'var(--color-danger)' : undefined,
                    fontSize: 11,
                    padding: '4px 8px',
                    width: 'auto',
                  }}
                >
                  <Icon name="x" size={11} /> Supprimer
                </button>
              </div>
            )}

            <div className={styles.tree}>
              {loadingTree && !tree && <div style={{ padding: 12, fontSize: 12, color: 'var(--color-text-muted)' }}>Chargement…</div>}
              {tree && (
                <FileTreeView
                  node={tree}
                  path={tree.path ?? '/'}
                  openFolders={openFolders}
                  selectedPath={selectedPath}
                  modByPath={modByPath}
                  onToggleFolder={toggleFolder}
                  onSelectPath={selectPath}
                  multiSelectMode={multiSelectMode}
                  selectedForDelete={selectedForDelete}
                  onToggleMultiSelect={toggleSelectionFor}
                />
              )}
            </div>

            <div className={styles.treeFooter}>
              {selectedPath ? <span className={styles.treePath}>{selectedPath}</span> : <span className={styles.treePathMuted}>racine sélectionnée</span>}
            </div>
          </aside>

          <section className={styles.fileView}>
            {selectedNode ? (
              <>
                <header className={styles.fileHeader}>
                  <div className={styles.fileHeaderLeft}>
                    <Icon name={selectedNode.type === 'folder' ? 'layers' : 'git-commit'} size={14} />
                    <div>
                      <div className={styles.fileName}>{selectedNode.name}</div>
                      <div className={styles.fileMeta}>
                        {selectedNode.type === 'folder'
                          ? `${selectedNode.children?.length ?? 0} élément${(selectedNode.children?.length ?? 0) > 1 ? 's' : ''}`
                          : `${selectedNode.size ?? ''}${selectedNode.readonly ? ' · binaire/trop volumineux' : ''}`}
                      </div>
                    </div>
                  </div>
                  <div className={styles.fileHeaderActions}>
                    {selectedIsFile && !selectedNode.readonly && content != null && !contentError && (
                      <>
                        <button className={styles.actionBtn} onClick={() => setEditContent(content ?? '')} disabled={saving || editContent === (content ?? '')} title="Annuler les modifs non sauvegardées">
                          <Icon name="x" size={11} /> Annuler
                        </button>
                        <button className={[styles.actionBtn, styles.actionPrimary].join(' ')} onClick={saveEdit} disabled={saving || editContent === (content ?? '')}>
                          <Icon name="check" size={11} /> {saving ? 'Sauvegarde…' : 'Enregistrer'}
                        </button>
                      </>
                    )}
                    <button className={styles.actionBtn} onClick={renameSelected} disabled={selectedPath === '/' || saving || renaming}>
                      <Icon name="pencil" size={11} /> {renaming ? 'Renommage…' : 'Renommer'}
                    </button>
                    {selectedIsFile && (
                      <button className={styles.actionBtn} onClick={downloadSelected}>
                        <Icon name="deploy" size={11} style={{ transform: 'rotate(180deg)' }} /> Télécharger
                      </button>
                    )}
                    <button
                      className={[styles.actionBtn, styles.actionDanger].join(' ')}
                      onClick={() => setDeleteTarget(selectedPath)}
                      disabled={selectedPath === '/' || saving}
                    >
                      <Icon name="x" size={11} /> Supprimer
                    </button>
                  </div>
                </header>

                {selectedIsFile ? (
                  selectedNode.readonly ? (
                    <div className={styles.fileEmpty}>
                      <Icon name="warn" size={20} />
                      <span>Fichier binaire ou trop gros (&gt;1MB) — édition non disponible. Utilise "Télécharger".</span>
                    </div>
                  ) : loadingContent ? (
                    <pre className={styles.fileContent}>// Chargement…</pre>
                  ) : contentError ? (
                    <pre className={styles.fileContent}>// {contentError}</pre>
                  ) : (
                    <textarea
                      className={styles.fileEditor}
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== 'Tab' || e.shiftKey) return;
                        e.preventDefault();
                        const ta = e.currentTarget;
                        const start = ta.selectionStart;
                        const end = ta.selectionEnd;
                        setEditContent(editContent.slice(0, start) + '  ' + editContent.slice(end));
                        requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = start + 2; });
                      }}
                      spellCheck={false}
                    />
                  )
                ) : (
                  <div className={styles.folderView}>
                    <div className={styles.folderLabel}>Contenu du dossier</div>
                    <ul className={styles.folderChildren}>
                      {(selectedNode.children ?? []).length === 0 && <li className={styles.folderEmpty}>Dossier vide.</li>}
                      {(selectedNode.children ?? []).map((c) => {
                        const childPath = c.path ?? (selectedPath === '/' ? '/' + c.name : selectedPath + '/' + c.name);
                        return (
                          <li key={c.name} onClick={() => selectPath(childPath)}>
                            <Icon name={c.type === 'folder' ? 'layers' : 'git-commit'} size={11} />
                            <span>{c.name}</span>
                            {c.size && <em>{c.size}</em>}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.fileEmpty}>
                <Icon name="layers" size={20} />
                <span>Sélectionner un fichier ou un dossier</span>
              </div>
            )}
          </section>
        </div>
    </>
  );

  const confirmModals = (
    <>
      {newFolderFor && (
        <Modal
          open onClose={() => setNewFolderFor(null)}
          title="Nouveau dossier"
          subtitle={`Créer dans ${newFolderFor}`}
          width="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setNewFolderFor(null)}>Annuler</Button>
              <Button variant="primary" onClick={confirmNewFolder} disabled={!newFolderName.trim() || saving}>Créer</Button>
            </>
          }
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--color-text-muted)', letterSpacing: '0.07em', fontWeight: 600 }}>Nom du dossier</span>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void confirmNewFolder(); }}
              autoFocus
              placeholder="mon-dossier"
              style={{
                background: 'var(--color-background-secondary)',
                border: '1px solid var(--color-border)',
                borderRadius: 6,
                padding: '10px 12px',
                fontSize: 14,
                fontFamily: 'var(--font-family-mono)',
                color: 'var(--color-text)',
              }}
            />
          </label>
        </Modal>
      )}

      {newFileFor && (
        <Modal
          open onClose={() => setNewFileFor(null)}
          title="Nouveau fichier texte"
          subtitle={`Créer dans ${newFileFor}`}
          width="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setNewFileFor(null)}>Annuler</Button>
              <Button variant="primary" onClick={confirmNewFile} disabled={!newFileName.trim() || saving}>Créer & éditer</Button>
            </>
          }
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--color-text-muted)', letterSpacing: '0.07em', fontWeight: 600 }}>Nom du fichier <em style={{ fontStyle: 'normal', opacity: 0.6 }}>extension incluse</em></span>
            <input
              type="text"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void confirmNewFile(); }}
              autoFocus
              placeholder="config.json"
              style={{
                background: 'var(--color-background-secondary)',
                border: '1px solid var(--color-border)',
                borderRadius: 6,
                padding: '10px 12px',
                fontSize: 14,
                fontFamily: 'var(--font-family-mono)',
                color: 'var(--color-text)',
              }}
            />
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
              Le fichier sera créé vide et passé direct en mode édition.
            </span>
          </label>
        </Modal>
      )}

      {deleteTarget && (
        <Modal
          open onClose={() => setDeleteTarget(null)}
          title="Supprimer"
          subtitle={deleteTarget}
          width="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Annuler</Button>
              <Button variant="danger" onClick={confirmDelete} leftIcon={<Icon name="x" size={12} />} disabled={saving}>Supprimer</Button>
            </>
          }
        >
          <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
            La suppression de <strong style={{ color: 'var(--color-text)' }}>{baseName(deleteTarget)}</strong> sera enregistrée comme modification en attente.
            Tu pourras l'annuler depuis la page du container.
          </div>
        </Modal>
      )}

      {bulkDeleteOpen && (
        <Modal
          open onClose={() => { if (!saving) { setBulkDeleteOpen(false); setBulkDeleteProgress(null); } }}
          title={`Supprimer ${selectedForDelete.size} élément${selectedForDelete.size > 1 ? 's' : ''} ?`}
          subtitle="Suppression en masse — enfants supprimés avant les parents"
          width="md"
          footer={
            bulkDeleteProgress && bulkDeleteProgress.done === bulkDeleteProgress.total ? (
              <Button variant="primary" onClick={() => { setBulkDeleteOpen(false); setBulkDeleteProgress(null); exitMultiSelect(); }}>
                Fermer
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setBulkDeleteOpen(false)} disabled={saving}>Annuler</Button>
                <Button variant="danger" onClick={confirmBulkDelete} leftIcon={<Icon name="x" size={12} />} disabled={saving}>
                  {saving ? `Suppression… ${bulkDeleteProgress?.done ?? 0}/${bulkDeleteProgress?.total ?? selectedForDelete.size}` : `Supprimer ${selectedForDelete.size}`}
                </Button>
              </>
            )
          }
        >
          {!bulkDeleteProgress && (
            <>
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 10 }}>
                Chaque suppression sera enregistrée comme modification en attente. Tu pourras les annuler individuellement depuis la page du container.
              </div>
              <div style={{
                maxHeight: 300,
                overflowY: 'auto',
                background: 'var(--color-background-secondary)',
                border: '1px solid var(--color-border)',
                borderRadius: 6,
                padding: 8,
                fontSize: 12,
                fontFamily: 'var(--font-family-mono)',
              }}>
                {Array.from(selectedForDelete).sort().map((p) => (
                  <div key={p} style={{ padding: '2px 0', color: 'var(--color-text)' }}>{p}</div>
                ))}
              </div>
            </>
          )}
          {bulkDeleteProgress && (
            <div>
              <div style={{ marginBottom: 10, fontSize: 13 }}>
                <strong>{bulkDeleteProgress.done}</strong> / {bulkDeleteProgress.total} traités
                {bulkDeleteProgress.errors.length > 0 && (
                  <span style={{ color: 'var(--color-danger)', marginLeft: 8 }}>· {bulkDeleteProgress.errors.length} erreur(s)</span>
                )}
              </div>
              {/* Barre de progression */}
              <div style={{ background: 'var(--color-background-secondary)', borderRadius: 4, overflow: 'hidden', height: 6, marginBottom: 10 }}>
                <div style={{
                  background: bulkDeleteProgress.errors.length > 0 ? 'var(--color-warning)' : 'var(--color-success)',
                  height: '100%',
                  width: `${(bulkDeleteProgress.done / bulkDeleteProgress.total) * 100}%`,
                  transition: 'width 0.2s',
                }} />
              </div>
              {bulkDeleteProgress.errors.length > 0 && (
                <div style={{
                  background: 'var(--color-danger-bg)',
                  color: 'var(--color-danger)',
                  borderRadius: 6,
                  padding: 8,
                  fontSize: 11,
                  maxHeight: 200,
                  overflowY: 'auto',
                  fontFamily: 'var(--font-family-mono)',
                }}>
                  {bulkDeleteProgress.errors.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              )}
            </div>
          )}
        </Modal>
      )}
    </>
  );

  if (embedded) {
    return <div className={styles.embeddedFiles}>{shell}{confirmModals}</div>;
  }
  return (
    <div className={styles.backdrop} onMouseDown={onClose} role="dialog" aria-modal>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>{shell}</div>
      {confirmModals}
    </div>
  );
}

interface FileTreeViewProps {
  node: FileNodeRemote;
  path: string;
  openFolders: Set<string>;
  selectedPath: string;
  modByPath: (path: string) => FileModificationRemote | undefined;
  onToggleFolder: (path: string) => void;
  onSelectPath: (path: string) => void;
  multiSelectMode: boolean;
  selectedForDelete: Set<string>;
  onToggleMultiSelect: (path: string) => void;
  depth?: number;
}
function FileTreeView({
  node, path, openFolders, selectedPath, modByPath, onToggleFolder, onSelectPath,
  multiSelectMode, selectedForDelete, onToggleMultiSelect, depth = 0,
}: FileTreeViewProps) {
  const isFolder = node.type === 'folder';
  const isOpen = openFolders.has(path);
  const isSelected = selectedPath === path;
  const isMultiSelected = selectedForDelete.has(path);
  const isRoot = path === '/';
  const mod = modByPath(path) ?? (node.modificationKind ? {
    path,
    type: node.modificationKind,
    kind: node.type,
    at: '',
    by: '',
  } as FileModificationRemote : undefined);
  return (
    <>
      <button
        className={[styles.treeRow, isSelected && styles.treeSelected].filter(Boolean).join(' ')}
        style={{
          paddingLeft: 8 + depth * 14,
          background: isMultiSelected ? 'var(--color-primary-light)' : undefined,
        }}
        onClick={() => {
          if (multiSelectMode && !isRoot) {
            onToggleMultiSelect(path);
          } else {
            onSelectPath(path);
            if (isFolder) onToggleFolder(path);
          }
        }}
      >
        {multiSelectMode && !isRoot && (
          <input
            type="checkbox"
            checked={isMultiSelected}
            onChange={() => onToggleMultiSelect(path)}
            onClick={(e) => e.stopPropagation()}
            style={{ marginRight: 2, accentColor: 'var(--color-primary)', cursor: 'pointer' }}
          />
        )}
        {isFolder ? <Icon name={isOpen ? 'chevron-down' : 'chevron-right'} size={10} /> : <span className={styles.treeDot} />}
        <Icon name={isFolder ? 'layers' : 'git-commit'} size={10} />
        <span className={styles.treeLabel}>{node.name === '/' ? '/' : node.name}</span>
        {mod && (
          <span
            title={mod.type}
            style={{
              fontSize: 9,
              fontWeight: 600,
              padding: '1px 5px',
              borderRadius: 3,
              background: mod.type === 'added' ? 'rgba(16,185,129,0.2)' : mod.type === 'modified' ? 'rgba(245,158,11,0.2)' : 'rgba(239,68,68,0.2)',
              color: mod.type === 'added' ? '#6EE7B7' : mod.type === 'modified' ? '#FCD34D' : '#FCA5A5',
              marginLeft: 'auto',
            }}
          >
            {mod.type === 'added' ? '+' : mod.type === 'modified' ? '~' : '−'}
          </span>
        )}
        {!mod && node.size && <span className={styles.treeSize}>{node.size}</span>}
      </button>
      {isFolder && isOpen && node.children?.map((child) => {
        const childPath = child.path ?? (path === '/' ? '/' + child.name : path + '/' + child.name);
        return (
          <FileTreeView
            key={child.name}
            node={child}
            path={childPath}
            openFolders={openFolders}
            selectedPath={selectedPath}
            modByPath={modByPath}
            onToggleFolder={onToggleFolder}
            onSelectPath={onSelectPath}
            multiSelectMode={multiSelectMode}
            selectedForDelete={selectedForDelete}
            onToggleMultiSelect={onToggleMultiSelect}
            depth={depth + 1}
          />
        );
      })}
    </>
  );
}
